import { bool, func, number, string } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { AvatarLarge, InlineTextButton, NamedLink, NotificationBadge } from '../../components';

import css from './TopbarMobileMenu.module.css';

// Common navigation links for both authenticated and unauthenticated users
const NAVIGATION_LINKS = [
  { name: 'AboutPage', labelId: 'Footer.toAboutPage' },
  { name: 'FixDeafAccessPage', labelId: 'Footer.toFixDeafAccessPage' },
  { name: 'CMSPage', params: { pageId: 'invest' }, labelId: 'Footer.toInvestPage' },
  { name: 'CommProfJoinPage', labelId: 'CommProfJoinPage.commprofTabTitle' },
  { name: 'DeafPage', label: 'Deaf Community' },
  { name: 'OurServicesPage', labelId: 'Footer.toOurServicesPage' },
  { name: 'CMSPage', params: { pageId: 'our-co-op' }, labelId: 'Footer.toOurCoopPage' },
  { name: 'CMSPage', params: { pageId: 'social-impact' }, labelId: 'Footer.toSocialImpact' },
  { name: 'JoinUsPage', labelId: 'Footer.toJoinUsPage' },
  { name: 'GetInvolvedPage', labelId: 'Footer.toGetInvolvedPage' },
  { name: 'CMSPage', params: { pageId: 'blog' }, labelId: 'Footer.toBlogPage' },
  { name: 'ContactPage', labelId: 'Footer.toContactPage' },
];

// Helper function to render navigation links
const renderNavigationLinks = (currentPageClass, currentPage) =>
  NAVIGATION_LINKS.map((link, index) => {
    // Check if this link is the current page
    // For CMSPage, we need to check both the page name and the pageId parameter
    let isCurrentPage = false;
    if (link.name === 'CMSPage' && link.params?.pageId) {
      // For CMS pages, check if we're on a CMSPage and extract the pageId from the URL
      isCurrentPage = currentPage === 'CMSPage' &&
        window.location.pathname.includes(`/${link.params.pageId}`);
    } else {
      isCurrentPage = currentPageClass ? !!currentPageClass(link.name) : false;
    }

    return (
      <NamedLink
        key={`${link.name}-${link.params?.pageId || index}`}
        className={classNames(css.navigationLink, isCurrentPage ? css.currentPage : null)}
        name={link.name}
        params={link.params}
      >
        {link.labelId ? <FormattedMessage id={link.labelId} /> : link.label}
      </NamedLink>
    );
  });

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage = null,
    currentUserHasListings,
    currentUser = null,
    notificationCount = 0,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);

  // userTypes for UserNav options
  const privateData = user.attributes.profile.privateData || {};
  const userTypes = privateData.userTypes || [];
  const isUserProvider = userTypes.includes('communicationProfessional');
  const isUserCustomer = userTypes.includes('customer');
  const isUserContractCustomer = userTypes.includes('contractCustomer');
  const isUserDeaf = userTypes.includes('deafUser');
  const notCustomer = ['communicationProfessional'];

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
          <div className={css.customLinks}>
            {renderNavigationLinks(null, null)}
          </div>
        </div>
        <div className={css.footer}>
          <NamedLink
            className={classNames(css.createNewListingLink, css.requirement)}
            name="DirectoryPage"
          >
            <span className={css.icon}>videocam</span>
            <FormattedMessage id="TopbarMobileMenu.makeVideoCall" />
          </NamedLink>
          <NamedLink
            className={classNames(css.createNewListingLink, css.requirement)}
            name="CreateRequirementPage"
          >
            <span className={css.icon}>person</span>

            <FormattedMessage id="TopbarMobileMenu.createbooking" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.userLinks}>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageBookingsPage'))}
            name="ManageBookingsPage"
          >
            <FormattedMessage id="TopbarDesktop.manageBookings" />
          </NamedLink>
          {isUserProvider ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
              name="SearchPage"
              to={{
                search: 'pub_listingtype=requirement',
              }}
            >
              <FormattedMessage id="TopbarMobileMenu.browseBookingsLink" />
            </NamedLink>
          ) : null}
          {isUserProvider ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          ) : null}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('FavouritesPage'))}
            name="FavouritesPage"
          >
            <FormattedMessage id="TopbarMobileMenu.FavouritesLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </div>

        <div className={css.customLinks}>
          {renderNavigationLinks(currentPageClass, currentPage)}
        </div>
      </div>
      {isUserProvider ? (
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      ) : null}

        <div className={css.footer}>
          {isUserDeaf && (
            <NamedLink
              className={classNames(css.createNewListingLink, css.requirement)}
              name="DirectoryPage"
            >
              <span className={css.icon}>videocam</span>
              <FormattedMessage id="TopbarMobileMenu.makeVideoCall" />
            </NamedLink>
          )}
          {isUserContractCustomer && isAuthenticated && (
            <NamedLink
              className={classNames(css.createNewListingLink, css.requirement)}
              name="VideoConferencePage"
            >
              <span className={css.icon}>videocam</span>
              <FormattedMessage id="TopbarDesktop.videoConference" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.createNewListingLink, css.requirement)}
            name="CreateRequirementPage"
          >
            <span className={css.icon}>person</span>
            <FormattedMessage id="TopbarDesktop.createRequirementListing" />
          </NamedLink>

        </div>
    </div>
  );
};

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
